import classNames from "classnames";
import { useCallback, useEffect, useState } from "react";
// Libraries
import { useNavigate } from "react-router";

// types
import { CURRENCY, ECONTRACT_STATUS, USER_ROLE } from "../../enums";
import {
  ESenderRole,
  IContractDetailsResponseData,
} from "../../setup/api/services/contract/models";
import { IUserInfoResponseData } from "../../setup/api/services/user/models";
import { LOCALIZE_DATE, ROUTES } from "../../setup/constants";
// providers and context
import { BuyOperation } from "./buy";
// constants and translations
import { REQUIRED_AMOUNT } from "./constants";
// styles
import styles from "./item.module.scss";
import { SellOperation } from "./sell";
import { ActiveStatus } from "./status/active";
import { ArbitrationStatus } from "./status/arbitration";
import { ClosedStatus } from "./status/closed";
import { CompletedStatus } from "./status/completed";
import { WaitingInspectionStatus } from "./status/inspection";
import { ItemAcceptedStatus } from "./status/item_accepted";
import { ResolvedStatus } from "./status/resolve";
import { WaitingDeliveryStatus } from "./status/waiting_delivery";
import { User } from "./user";

export const options: Record<string, string> = {
  weekday: "long",
  year: "numeric",
  month: "long",
  day: "numeric",
};

interface Props {
  smallerSection?: boolean;
  contract: IContractDetailsResponseData;
  userId: string;
}

const ContractsListItem = ({ contract, userId }: Props): JSX.Element => {
  const navigate = useNavigate();
  const [beneficiary, setBeneficiary] = useState<
    IUserInfoResponseData | undefined
  >(undefined);

  const getContractType = useCallback(() => {
    if (
      (contract.senderId == userId &&
        contract.senderRole === ESenderRole.PROVIDER) ||
      (contract.senderId != userId &&
        contract.senderRole === ESenderRole.CUSTOMER)
    ) {
      return USER_ROLE.SELLER;
    } else {
      return USER_ROLE.BUYER;
    }
  }, [contract.senderId, contract.senderRole, userId]);

  const onContractClick = useCallback(
    (contractId: string): void => {
      navigate(`${ROUTES.CONTRACT}${contractId}`);
    },
    [navigate]
  );

  const renderCalculatedPrice = useCallback((): string => {
    const price =
      Number(contract?.itemPrice) + Number(contract?.shipment?.fee || 0);
    return price.toFixed(2);
  }, [contract?.itemPrice, contract?.shipment?.fee]);

  useEffect(() => {
    setBeneficiary(
      contract.senderId == userId ? contract.customer : contract.provider
    );
  }, [contract, userId]);

  const imageSection = useCallback(() => {
    const imageExists =
      contract?.itemDetail?.images?.length &&
      contract?.itemDetail?.images[0] !== "";
    return (
      <div className={styles.imgContainer}>
        <img
          className={classNames(
            !imageExists ? styles.placeholder : styles.item_image
          )}
          src={
            imageExists
              ? contract?.itemDetail?.images[0]
              : "/assets/images/svg/contract_placeholder.svg"
          }
          alt=""
        />
      </div>
    );
  }, [contract?.itemDetail?.images]);

  const isEscalationStatus = useCallback(() => {
    return [
      ECONTRACT_STATUS.INSPECTION_ESCALATION,
      ECONTRACT_STATUS.DELIVERY_ESCALATION,
    ].includes(contract.status);
  }, [contract.status]);
  const isResolvedStatus = useCallback(() => {
    return [
      ECONTRACT_STATUS.RESOLVED_REFUND_CUSTOMER,
      ECONTRACT_STATUS.RESOLVED_RELEASE_PROVIDER,
    ].includes(contract.status);
  }, [contract.status]);
  const isClosedStatus = useCallback(() => {
    return [
      ECONTRACT_STATUS.CLOSED_REFUND_CUSTOMER,
      ECONTRACT_STATUS.CLOSED_RELEASE_PROVIDER,
    ].includes(contract.status);
  }, [contract.status]);

  const detailsSection = useCallback(() => {
    return (
      <div className={styles.contract_info}>
        <div className={styles.status_wrapper}>
          {/* user role for contract */}
          {getContractType() === USER_ROLE.BUYER && <BuyOperation />}
          {getContractType() === USER_ROLE.SELLER && <SellOperation />}
          {/* contract status */}
          {contract.status === ECONTRACT_STATUS.CREATED && <ActiveStatus />}
          {contract.status === ECONTRACT_STATUS.COMPLETED && (
            <CompletedStatus />
          )}
          {contract.status === ECONTRACT_STATUS.WAITING_FOR_DELIVERY && (
            <WaitingDeliveryStatus />
          )}
          {contract.status === ECONTRACT_STATUS.ITEM_INSPECTION && (
            <WaitingInspectionStatus />
          )}
          {contract.status === ECONTRACT_STATUS.ITEM_ACCEPTED && (
            <ItemAcceptedStatus />
          )}
          {isResolvedStatus() && <ResolvedStatus />}
          {isClosedStatus() && <ClosedStatus />}
          {isEscalationStatus() && <ArbitrationStatus />}
        </div>
        <div className={styles.header_wrapper}>
          <h4 className={styles.header_title}>{contract.itemDetail?.title}</h4>
          <p className={classNames("hint2", "mr1", "ml1")}>.</p>
          <p className={"hint2"}>
            {new Date(contract.createdAt).toLocaleDateString(
              LOCALIZE_DATE,
              options
            )}
          </p>
        </div>
        <p className={classNames("p2", styles.contractDescription)}>
          {contract.itemDetail?.description}
        </p>
      </div>
    );
  }, [
    contract.createdAt,
    contract.itemDetail?.description,
    contract.itemDetail?.title,
    contract.status,
    getContractType,
    isClosedStatus,
    isEscalationStatus,
    isResolvedStatus,
  ]);

  const profileSection = useCallback(() => {
    return (
      <div className={styles.profile_section}>
        <div className={styles.priceContainer}>
          <>
            <div>
              {beneficiary && (
                <User
                  name={beneficiary?.profile?.firstName}
                  phoneNumber={beneficiary?.phoneNumber}
                  imageUrl={beneficiary?.profile?.photoUrl}
                />
              )}
            </div>
            {beneficiary && <div className={styles.divider} />}
          </>

          <div className={styles.priceSection}>
            <div className={"hint2"}>{REQUIRED_AMOUNT}</div>
            <span className={"subtitle1"}>{renderCalculatedPrice()}</span>
            <span> </span>
            <span className={"hint2"}>{CURRENCY.SAR}</span>
          </div>
        </div>
      </div>
    );
  }, [beneficiary, renderCalculatedPrice]);

  const navigationSection = useCallback(() => {
    return (
      <div className={styles.contract_navigation}>
        <img src="/assets/images/svg/contractNavigation.svg" alt="navigation" />
      </div>
    );
  }, []);

  return (
    <div key={contract.id} className={"card"}>
      <div
        className={styles.contractLink}
        onClick={() => {
          onContractClick(contract.id);
        }}>
        <div className={styles.left_section}>
          {imageSection()}
          {detailsSection()}
        </div>
        <div className={styles.right_section}>
          {profileSection()}
          {navigationSection()}
        </div>
      </div>
    </div>
  );
};

export default ContractsListItem;
